import { Country, RegUser, QuoteType, RegUserMigration } from '@common/types';
import { HealthMigrationResponse } from '@services/apis/insure';

export type SumInsuredValue = number | 'unlimited';

export interface FastPlanForm {
  members: HealthMember[];
  pincode: number;
  ped: string[];
  sumInsured: number;
  deductibleAmount?: number;
  planType?: string;
  porting?: boolean;
  policy_start_date?: Date | null;
  SelfAge?: number;
  loyalty_discount?: boolean;
  existing_policy_number?: string;
}

export interface HealthPremiumList {
  sumInsured: SumInsuredValue;
  premiums: HealthPremium[];
  addOns: HealthAddOn[];
}

export interface UserSliceState {
  /**
   * User is using normal quote or fast quote
   */
  quote: QuoteType;
  /**
   * To track the current step the user is on
   */
  step: number;
  /**
   * Calculate the progress whenever step changes
   */
  stepProgress: number;
  /**
   * User quote details
   */
  plan: HealthQuotePlanDetails;
  /**
   * List of premiums received from API
   */
  premiums: HealthPremiumList[];
  /**
   * Current premium for the plan
   */
  premium: HealthPremium | undefined;
  /**
   * Loading progress for premium API requests
   */
  premiumApiProgress: number;
  /**
   * Premium API error
   */
  premiumApiError: string;
  /**
   * Loading state for suggestion API requests
   */
  loadingSuggestion: boolean;
  /**
   * Suggestion API error
   */
  suggestionError: string;
  /**
   * Loading state for estimate API requests
   */
  loadingEstimate: boolean;
  /**
   * Suggestion/estimate received
   */
  suggestion: Suggestion | null;
  /**
   * List of add ons user can opt for with premium
   */
  addOns: HealthAddOns;
  /**
   * Loading state for addons API requests
   */
  loadingAddOns: boolean;
  /**
   * Loading state for premium API requests
   */
  loadingPremium: boolean;
  /**
   * The policy details generated once quote is finalized
   */
  policy: any;
  /**
   * Whether premium has been fetched at-least once
   */
  premiumFetched: boolean;
  /**
   * finalizing is in progress or not
   */
  finalizing: boolean;
  /**
   * User selected add on to view details
   */
  selectedAddOn: string | undefined;
  /**
   * if there are any validation issue or addon warnings to be shown to user
   */
  planWarning: string | undefined;
  /**
   * Addon selection change warning with structured data
   */
  addOnSelectionWarning:
    | {
        addons: string[];
        description: string;
      }
    | undefined;
  /**
   * Enable emi for the plan using params
   */
  emiEnabled: boolean;
  /**
   * Enable deductibles for the plan using params
   */
  deductiblesEnabled: boolean;
  /**
   * Previous Policy Number for  Migration
   */
  previousPolicyNumber: string;
  /**
   * Previous Policy Details
   */
  previousPolicyDetails: HealthMigrationResponse | null;
  /**
   * Loading state for previous policy details
   */
  loadingPreviousPolicyDetails: boolean;
  /**
   * Number array + 'unlimited' of possible sum insured values.
   */
  sumInsuredList: SumInsuredValue[];
  /**
   * Number array + 'unlimited' of unavailable covers
   */
  unavailableCoversList: SumInsuredValue[];
  /**
   * Migration E mail id
   */
  migrationEmailId: string;
  /**
   * Pincode validation state
   */
  validatingPincode: boolean;
  /**
   * Pincode validation error
   */
  pincodeError: string;
  /**
   * Whether the form is valid or not
   */
  planFormValid: boolean;
  /**
   * Set whether the form is valid or not
   */
  setPlanFormValid: (isValid: boolean) => void;
  setMemberNri: (index: number, value: boolean) => void;
  setCombo: (combo: HealthMemberCombo, maxAdults: number) => void;
  setAdults: (members: HealthMember[]) => void;
  addMember: (member: HealthQuoteMember, gender?: HealthQuoteMemberGender) => void;
  setMemberGender: (
    member: HealthQuoteMember,
    gender: HealthQuoteMemberGender
  ) => void;
  removeMember: (member: HealthQuoteMember) => void;
  resetDraftMembers: () => void;
  setMemberDiseases: (
    index: number,
    diseases: string[],
    syncAddOns?: boolean
  ) => void;
  setDraftMemberDiseases: (
    index: number,
    diseases: string[],
    syncAddOns?: boolean
  ) => void;
  setMemberAddOns: (index: number, addOns: HealthMemberAddOn[]) => void;
  setDraftMemberAddOns: (index: number, addOns: HealthMemberAddOn[]) => void;
  updatePlanDetails: (
    members: HealthMember[],
    pincode: number,
    ped: string[]
  ) => void;
  setFastPlanDetails: (fastPlan: FastPlanForm, formIsValid?: boolean) => void;
  setSumInsured: (sumInsured: SumInsuredValue) => void;
  clearSuggestion: () => void;
  updateUserSumInsured: (latestSumInsured: SumInsuredValue) => void;
  getCoverSuggestion: () => Promise<void>;
  getEstimate: (premiumChangeInfo?: boolean) => Promise<void>;
  setStep: (latestStep: number) => void;
  setUserProduct: (latestProduct: string) => void;
  setFastQuote: (
    latestProduct: string,
    emiEnabled?: boolean,
    deductiblesEnabled?: boolean
  ) => void;
  getPremium: () => Promise<void>;
  toggleAddOn: (
    addOnKey: string,
    included: boolean,
    options?: {
      subOptionKey: string;
      value: HealthAddOnSubOptionValue;
    }
  ) => void;
  toggleModalAddOn: (
    addOnKey: string,
    members: HealthMember[],
    include: boolean
  ) => void;
  finalizeHealthQuote: (user: RegUser) => void;
  resetHealthPlan: (clearAll?: boolean) => void;
  updateEmiFrequency: (emiOption: HealthEMIFrequency) => void;
  toggleEmi: (enabled: boolean) => void;
  updateTenure: (tenure: number) => Promise<void>;
  toggleAddOnDetail: (addOnKey?: string) => void;
  clearPlanWarning: () => void;
  clearAddOnSelectionWarning: () => void;
  clearPremiums: (keepFetchedInfo?: boolean) => void;
  resetProduct: () => void;
  setPreviousPolicyNumber: (previousPolicyNumber: string) => void;
  setSumInsuredForMigration: (sumInsured: SumInsuredValue) => void;
  finalizeMigrationHealthQuote: (user: RegUserMigration) => void;
  setMigrationEmailId: (emailId: string) => void;
  clearPolicy: () => void;
  validatePincode: (fastPlan: FastPlanForm, formIsValid?: boolean) => Promise<void>;
}

export interface ProductSliceState {
  /**
   * Loading state for boundaries API requests
   */
  loadingProductBoundaries: boolean;
  /**
   * Error message incase boundaries API fails
   */
  productBoundariesError: string;
  /**
   * Loading state for features requests
   */
  loadingProductFeatures: boolean;
  /**
   * Product details list with product details, boundaries, frequencies
   */
  productsList: HealthProductDetails[];
  /**
   * Whether the most recently fetched boundaries are valid or not
   * it can become invalid incase the current sumInsured belongs
   * to unavailableCovers, other similar cases might need to be handled in the future.
   */
  boundariesInvalid: boolean;

  fetchProductBoundaries: (
    products: string[],
    tempPlan?: HealthQuotePlanDetails
  ) => Promise<void>;

  fetchProductFeatures: (
    products: string[],
    tempPlan?: HealthQuotePlanDetails
  ) => Promise<void>;
}

export type HealthQuoteStoreState = UserSliceState & ProductSliceState;

export const HEALTH_MEMBER_COMBO_SELF = 'You';
export const HEALTH_MEMBER_COMBO_PARENTS = 'Your Parents';
export const HEALTH_MEMBER_COMBO_FAMILY = 'Your Family';

export enum ComboType {
  You = 'You',
  Parents = 'Your Parents',
  Family = 'Your Family'
}

// These are the addons that support member selection
export type MemberSpecificAddons = 'abcd' | 'cc' | 'opd_care';

export interface HealthMember {
  relation: HealthQuoteMember;
  gender?: HealthQuoteMemberGender;
  age?: number;
  id?: number;
  diseases?: string[];
  /**
   * In case of new addons that support member selection, update MemberSpecificAddons.
   */
  opd_care?: boolean;
  abcd?: boolean;
  cc?: boolean;
  is_nri?: boolean;
  med_conditions?: string[];
  addons?: HealthMemberAddOn[];
  dependentRelation?: HealthQuoteMember[]; // to track which members are required to be present if this member is included
}

export type HealthMemberAddOnSubOptionKey =
  | 'rped_tenure'
  | 'hcb_cash'
  | 'med_conditions'
  | 'rmc_type'
  | 'abcd_members'
  | 'pth_amount';

interface HealthMemberAddOnSubOption {
  key: HealthMemberAddOnSubOptionKey;
  value: string;
}

export interface HealthMemberAddOn {
  key: string;
  included: boolean;
  sub_options?: HealthMemberAddOnSubOption[];
}

export interface HealthQuotePlanDetails {
  combo?: ComboType;
  members: HealthMember[];
  draftMembers: HealthMember[];
  kidsCount?: KidsCountObject;
  pincode: number | undefined;
  ped: string[];
  sumInsured: SumInsuredValue;
  deductible?: number;
  planType?: string;
  product: string;
  tenure?: number;
  optedEmi?: boolean;
  emiFrequency?: HealthEMIFrequency;
  mobile?: string;
  porting?: boolean;
  policy_start_date?: Date | null;
  hcb_cash?: number;
  pth_amount?: number;
  loyalty_discount?: boolean;
  existing_policy_number?: string;
}

export enum HealthEMIFrequency {
  HalfYearly = 'HalfYearly',
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  Yearly = 'Yearly'
}

export type HealthEMIOptions = {
  [key in HealthEMIFrequency]: number;
};

export interface HealthEMIOption {
  label: string;
  frequency: HealthEMIFrequency;
  premium: number;
}

export interface HealthAddOnPremiums {
  // sgb addon premium
  sgb?: number;
  // hcb addon premium
  hcb?: number;
  // cs addon premium
  cs?: number;
  // rped addon premium
  rped?: number;
  // urc addon premium
  urc?: number;
  // urn addon premium
  urn?: number;
  // ncb addon premium
  ncb?: number;
  // mty addon premium
  mty?: number;
}

export interface HealthPremium extends HealthAddOnPremiums {
  // for which tenure
  tenure: number;
  // Base plan premium
  basePlan: number;
  // total price
  total: number;
  // total tax
  tax: number;
  // discount amount
  discount?: number;
  // whether discount is included
  discountIncluded?: boolean;
  // emi rates for the tenure
  emi?: HealthEMIOptions;
  // whether the tenure premium was fetched successfully
  success?: boolean;
  // todo : new props that needs to be added in BE
  highlight?: string;
}

export interface DeductibleMap {
  [key: number]: number[];
}

export interface SuggestedHealthProduct {
  // unique key for the product
  key: string;
  // unique key for the insurer
  insurerKey: string;
  // whether the premium was retrieved successfully
  success: true;
  // premium
  premium: {
    basePlan: number;
    tax: number;
    total: number;
  };
  recommended: boolean;
}

export interface Suggestion {
  // suggested cover for the user based on their plan
  suggestedCover: number;
  // available covers for the user based on the inputs
  allCovers: number[];
  // which is the recommended insurer among the products
  recommendedInsurer: string;
  // List of products suggested
  products: SuggestedHealthProduct[];
}

export interface HealthAddOnSubOptionValue {
  label: string;
  value: string;
  exceptionList?: string[];
  inclusionList?: string[];
}
export interface HealthAddOnSubOption {
  key: string;
  values: HealthAddOnSubOptionValue[];
  type: 'radio' | 'select';
}

export enum HealthAddOnType {
  MANDATORY = 'mandatory',
  RECOMMENDED = 'recommended',
  OTHER = 'other'
}

export interface HealthAddOn {
  /*
   * Unique key for the addon
   */
  key: string;
  /**
   * Whether user is eligible for the addon
   */
  isEligible: boolean;
  /**
   * Why user is not eligible, applicable when isEligible is false
   */
  reason: string;
  /**
   * Name of the addon which can be shown to user
   */
  name: string;
  /**
   * Title to be shown in popups
   */
  title: string;
  /**
   * Description to be shown in popups based on plan variables, addon values
   */
  desc?: string;
  /**
   * Addon features
   */
  features: HealthAddOnFeature[];
  /**
   * Whether the addon is free or not
   */
  isFree: boolean;
  /**
   * Whether the addon is mandatory to be selected
   */
  mandatory: boolean;
  /**
   * Why the add on is mandatory ?
   */
  mandatoryWarning: string;
  /**
   * sub options like tenure etc if available for the rider
   */
  subOptions: HealthAddOnSubOption[];
  /**
   * User has included the addon to their plan
   */
  included: boolean;
  /**
   * rped addon tenure
   */
  subOptionValues?: { [key: string]: HealthAddOnSubOptionValue };
  /**
   * Premium for the addon
   */
  additionalPremium?: number;
  /**
   * This addon cannot be selected with other addons mentioned in the list
   */
  canNotSelectAlongWith?: string[];
  /**
   * Only one addon from this group can be selected at a time (mutually exclusive selection)
   * Example: ncb can have ['ncb_booster'] - meaning only one of ncb or ncb_booster can be selected
   */
  canSelectAlternatively?: string[];
  /**
   * This key is to identify riders which depend upon the selected conditions (Eg:Activ One - CC and OPD_CARE)
   * It is also used to identify addons which support member selection.
   */
  noDefaultOption?: boolean;
  /**
   *Sub add on values
   */
  subAddOns?: string;

  // todo : new props thats needs to be added in BE
  /**
   * Whether the addon is a recommended/mandatory/other addon or not
   */
  type: HealthAddOnType;
  /**
   * Extra info to be shown in the popups
   */
  extraInfo?: {
    title: string;
    description: string;
  };
  /**
   * Whether the addon has an info that needs to be highlighted
   */
  highlight?: string;
  /**
   * To display the selected option next to the addon name in the status page
   */
  selectedOption?: string;
  /**
   * Eligible members for the addon where member options is applicable. Ex: ABCD rider in ERGO OS
   */
  eligibleMembers?: HealthMember[];
  /**
   * Tenures (in years) for which this addon is applicable. Absent means all tenures.
   * E.g. [2, 3] hides the addon when 1-year tenure is selected.
   */
  applicableTenures?: number[];
}

export type HealthAddOns = HealthAddOn[];

export interface HealthProduct {
  // unique product key
  key: string;
  // product name
  name: string;
  // insurers unique key
  insurerKey: string;
  // insurer's name
  insurerName: string;
  // logo name
  logo: string;
  // uin number
  uin: string;
  // brochure link
  brochure: string;
  // policy wordings link
  policyWording: string;
  // claim settlement ratio
  csr: number;
  // room rent
  rr: string;
  // no claim bonus
  ncb: number;
  // restoration bonus
  restoration: number;
  // ditto website link
  policyPage?: string;
}

export interface HealthProductFeature {
  icon: string;
  desc: string;
}

export interface HealthProductMainFeature {
  icon: string;
  title: string;
  desc: string;
  info?: string;
  specialBenefit?: boolean;
}

export interface HealthProductCardFeature {
  icon: string;
  text: string;
  isUsp?: boolean;
  isDefect?: boolean;
}

export interface HealthProductWaitingPeriod {
  title: string;
  desc: string;
  icon: string;
  list?: string[];
}

export interface HealthProductMissingFeature {
  text: string;
  icon: string;
}

export interface HealthAddOnFeature {
  title: string;
  desc: string;
  icon: string;
}

export interface HealthProductFeatures {
  // features shown in the policy details page first fold
  features?: HealthProductFeature[];
  // main benefits for the policy details page
  mainBenefits?: {
    // sub title for the section
    sub: string;
    items: HealthProductMainFeature[];
  };
  // extra benefits for the policy details page
  extraBenefits?: {
    // sub title for the section
    sub: string;
    items: HealthProductMainFeature[];
  };
  // waiting periods shown on the policy details page
  waitingPeriods?: HealthProductWaitingPeriod[];
  // not covered section in policy details page
  notCovered?: HealthProductMissingFeature[];
}

export interface MemberBoundary {
  relation: HealthQuoteMember;
  minAge: number;
  maxAge: number;
  dependentRelation?: HealthQuoteMember[];
}

export enum HealthProductType {
  Normal = 'normal',
  SuperTopUp = 'super-top-up',
  WithTypes = 'with-types'
}

export interface PlanTypeItem {
  key: string;
  name: string;
  description: string;
  features: {
    name: string;
    value: string;
  }[];
  policyWording: string;
  // ditto website link
  policyPage?: string;
}

export interface HealthBasicProductBoundary {
  // whether the product is normal / super top up / variant product
  type: HealthProductType;
  // members allowed in this product
  allowedMembers: MemberBoundary[];
  // allowed member combinations for this product
  allowedMemberCombinations: HealthQuoteMember[][];
  // what are the available combinations available for the product you, family, parents
  availableCombos: ComboType[];
  // available covers
  allCovers?: SumInsuredValue[];
  // unavailable covers
  unavailableCovers?: SumInsuredValue[];
  // available deductible map
  deductibles?: DeductibleMap;
  // available variants
  planTypes?: PlanTypeItem[];
  // total members allowed in the policy
  maxMembers: number;
  // maximum no of adults allowed
  maxAdults: number;
  // maximum no of kids allowed
  maxKids: number;
  // If at least one member should be above a particular age
  leastAge?: number;
  // is available for diabetic
  restrictDiabeticMembers: boolean;
  // if Loyalty Discount available
  loyaltyDiscountAvailable?: boolean;
  // Any discounts available for the product at the moment
  discounts?: string[];
  // if there are any restrictions related to tenure eg: Optima Super Secure is only available for 3 year tenure.
  tenureWarning?: string;
  // if emi option is available for this product - whether to display it will be decided by query params
  emiAvailable?: boolean;
  // if porting is available
  portingAvailable?: boolean;
  // Countries (with calling codes) the product is available at. Only applicable for NRI products.
  countries?: Country[];
  /**
   * Flag to display diseases input for members
   */
  conditionRequired?: boolean;
  /**
   * List of diseases/conditions
   */
  conditionList?: any[];
  /**
   * recommended cover for the product
   */
  recommendedCover?: number;
}

export interface HealthProductBoundary
  extends HealthBasicProductBoundary,
    HealthProductFeatures {
  // features to be shown on the product card
  cardFeatures?: HealthProductCardFeature[];
  // important features shown in fast quote
  importantFeatures?: HealthProductMainFeature[];
}

export interface HealthProductDetails {
  product: HealthProduct;
  boundaries: HealthProductBoundary;
}

export enum HealthQuoteMember {
  Self = 'Self',
  Spouse = 'Spouse',
  Father = 'Father',
  Mother = 'Mother',
  FatherInLaw = 'Father-in-law',
  MotherInLaw = 'Mother-in-law',
  Son = 'Son',
  Daughter = 'Daughter'
}

export enum HealthQuoteMemberGender {
  Male = 'Male',
  Female = 'Female'
}

export interface HealthMemberCombo {
  name: ComboType;
  description: string;
  members: HealthMember[];
}

export interface KidsCountObject {
  sons: number;
  daughters: number;
}
